import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Flex,
  FlexProps,
  FormLabel,
  Icon,
  BoxProps,
} from "@chakra-ui/react";
import {
  CreatableSelect,
  SingleValue,
  MultiValue,
  ChakraStylesConfig,
  Select,
  ActionMeta,
  OnChangeValue,
} from "chakra-react-select";
import { FiSearch } from "react-icons/fi";
import { motion, Transition } from "framer-motion";

const MotionFlex = motion<FlexProps>(Flex);
const MotionLabel = motion(FormLabel);
const MotionBox = motion<BoxProps>(Box);

export type Option = { label: string; value: string };

type KYDMultiSelectProps<T = Option> = {
  values: T[];
  onChange?: (values: T) => void;
  onAdd?: (value: T) => void;
  onRemove?: (value: T) => void;
  options?: T[];
  label?: string;
  placeholder?: string;
  createable?: boolean;
  onInputChange?: (input: string) => void;
  id?: string;
  height?: string;
  minHeight?: string;
  maxAllowed?: number;
  isDisabled?: boolean;
};

export default function KYDSingleSelect<T = Option>({
  values,
  onChange,
  options,
  label,
  placeholder,
  createable,
  onInputChange,
  onAdd,
  onRemove,
  isDisabled,
  id,
  height,
  minHeight = "48px",
  maxAllowed = 9999,
}: KYDMultiSelectProps<T>) {
  const [focused, setFocused] = useState(false);
  const hasValue = values.length > 0;
  const active = (focused || hasValue) && label;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const slide: Transition = { duration: 0.1, ease: "easeInOut" };

  const Component = useMemo(
    () => (createable ? CreatableSelect : Select),
    [createable]
  );

  const optionsFixed = useMemo(() => options, [createable, options]);

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  const handleInputChange = useCallback(
    (input: string) => {
      if (maxAllowed && values.length >= maxAllowed) {
        return;
      }
      onInputChange?.(input);
    },
    [onInputChange]
  );

  const handleChange = useCallback(
    (newValue: OnChangeValue<T, false>, actionMeta: ActionMeta<T>) => {
      onChange?.(newValue);
    },
    [onChange]
  );

  const chakraStyles: ChakraStylesConfig<T, false> = {
    container: (p) => ({ ...p, width: "100%", padding: 0 }),
    control: (p) => ({
      ...p,
      padding: 0,
      minHeight: "32px",
      border: "none",
      boxShadow: "none",
      outline: "none !important",
      ":focus-visible": {
        border: "none",
        boxShadow: "none",
      },
      "&[data-focus-visible]": {
        border: "none",
        boxShadow: "none",
      },
    }),
    valueContainer: (p) => ({ ...p, padding: 0, paddingLeft: 0 }),
    dropdownIndicator: () => ({ display: "none" }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (p) => ({
      ...p,
      margin: 0,
      padding: 0,
      color: "gray.500",
      paddingLeft: "2px",
    }),
    input: (p) => ({ ...p, margin: 0, padding: 0 }),
    multiValue: (p) => ({
      ...p,
      bg: "#EDF2F7",
      borderRadius: "4px",
      padding: "0 4px",
    }),
    multiValueLabel: (p) => ({ ...p, fontSize: "14px", padding: "2px 4px" }),
    multiValueRemove: (p) => ({
      ...p,
      ":hover": { bg: "#E2E8F0", color: "#4A5568" },
    }),
    menu: (p) => ({ ...p, zIndex: 9999 }),
  };

  return (
    <Box
      position="relative"
      width="100%"
      border="1px solid #E6E6E6"
      borderRadius="8px"
      bg="white"
      boxShadow="0px 10px 15px 0px #F7F7F7"
      p={3}
      key={`kyd-multiselect-box-${id}`}
    >
      <Icon
        as={FiSearch}
        position="absolute"
        left="12px"
        top="50%"
        transform="translateY(-50%)"
        color="rgba(150,150,150,0.7)"
        zIndex={2}
      />

      <MotionFlex
        layout
        minHeight={minHeight}
        height={height}
        transition={slide as any}
        direction={active ? "column" : "row"}
        align={active ? "stretch" : "center"}
        gap={active ? 0 : 2}
        pl="36px"
        id={`kyd-multiselect-flex-${id}`}
        key={`kyd-multiselect-flex-${id}`}
      >
        {label && (
          <MotionBox layout="position" transition={slide as any}>
            <MotionLabel
              id={`kyd-multiselect-label-${id}`}
              fontSize={active ? "12px" : "14px"}
              fontWeight={active ? "400" : "500"}
              color="#00000080"
              whiteSpace={active ? "normal" : "nowrap"}
              mb={0}
              mr={1}
              pointerEvents="none"
              display="flex"
              alignItems="center"
              key={`kyd-multiselect-label-${id}`}
            >
              {label}
            </MotionLabel>
          </MotionBox>
        )}

        <MotionBox
          layoutId={`kyd-multiselect-box-${id}`}
          key={`kyd-multiselect-box-${id}`}
          width="100%"
          layout="position"
          transition={slide as any}
        >
          <Select<T>
            onMenuOpen={() => {
              const duration = active ? 100 : 0;
              setTimeout(() => setIsMenuOpen(true), duration);
            }}
            isDisabled={isDisabled}
            onMenuClose={() => {
              setIsMenuOpen(false);
            }}
            inputId="kyd-multiselect"
            key={`kyd-multiselect-component-input-${id}`}
            id={`kyd-multiselect-component-input-${id}`}
            isClearable
            // menuIsOpen={
            //   options
            //     ? maxAllowed <= values.length
            //       ? false
            //       : isMenuOpen
            //     : false
            // }
            options={optionsFixed}
            value={values}
            onChange={handleChange}
            onInputChange={handleInputChange}
            placeholder={placeholder}
            chakraStyles={chakraStyles}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </MotionBox>
      </MotionFlex>
    </Box>
  );
}
